// Create a single formatter instance to be reused (Performance Optimization)
const mxnFormatter = new Intl.NumberFormat('es-MX', {
  style: 'currency',
  currency: 'MXN',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
});

/**
 * Formats a number to currency format with $ and commas
 * @param {number|string} value - The value to format
 * @returns {string} - Formatted string like "$3,000,000"
 */
export const formatCurrency = (value) => {
  if (value === '' || value === null || value === undefined) return '';
  
  // Remove any non-digit characters except decimal point and minus
  const numericValue = String(value).replace(/[^0-9.-]/g, '');
  
  if (numericValue === '' || numericValue === '-') return numericValue;
  
  // Parse to float to handle decimals
  const number = parseFloat(numericValue);
  
  if (isNaN(number)) return '';
  
  // Use the hoisted formatter
  return mxnFormatter.format(number);
};

/**
 * Parses a formatted currency string to a raw number string
 * Keeps decimal point for user input
 * @param {string} value - Formatted string like "$3,000,000" or "100.5"
 * @returns {string} - Clean numeric string like "100.5"
 */
export const parseCurrency = (value) => {
  if (value === '' || value === null || value === undefined) return '';
  
  // Remove currency symbol, commas, and spaces, but KEEP decimal point
  let numericString = String(value).replace(/[$,\s]/g, '');
  
  // Return empty string if nothing left
  if (numericString === '' || numericString === '-') return numericString;
  
  // Ensure only one decimal point
  const parts = numericString.split('.');
  if (parts.length > 2) {
    numericString = parts[0] + '.' + parts.slice(1).join('');
  }
  
  // Limit to 2 decimal places
  if (parts[1] && parts[1].length > 2) {
    numericString = parts[0] + '.' + parts[1].substring(0, 2);
  }
  
  return numericString;
};

/**
 * Formats input value for display (shows commas but NO $ symbol)
 * Shows decimals as user types them
 * @param {string|number} value - Current input value
 * @returns {string} - Formatted value for display WITHOUT $
 */
export const formatCurrencyInput = (value) => {
  if (value === '' || value === null || value === undefined) return '';
  
  // Convert to string
  const strValue = String(value);
  
  // If user is typing a decimal point, preserve it
  if (strValue.endsWith('.')) {
    const integerPart = strValue.slice(0, -1).replace(/[^0-9]/g, '');
    if (!integerPart) return '';
    const formatted = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return formatted + '.';
  }
  
  // Split into integer and decimal parts
  const parts = strValue.split('.');
  let integerPart = parts[0].replace(/[^0-9]/g, '');
  let decimalPart = parts[1];
  
  if (!integerPart) return '';
  
  // Format integer part with commas
  const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  
  // If there's a decimal part (user is typing)
  if (decimalPart !== undefined) {
    // Limit to 2 decimal places
    decimalPart = decimalPart.substring(0, 2);
    return formattedInteger + '.' + decimalPart;
  }
  
  // No decimal part - return just the formatted integer WITHOUT adding .00
  return formattedInteger;
};

/**
 * Formats a complete currency value with .00 if no decimals
 * Use this for displaying final values or on blur
 * @param {string|number} value - Value to format
 * @returns {string} - Formatted value with 2 decimal places
 */
export const formatCurrencyComplete = (value) => {
  if (value === '' || value === null || value === undefined) return '';
  
  const rawValue = String(value).replace(/[^0-9.-]/g, '');
  if (rawValue === '' || rawValue === '-') return rawValue;
  
  const number = parseFloat(rawValue);
  if (isNaN(number)) return '';
  
  // Format with commas and always 2 decimals
  const parts = number.toFixed(2).split('.');
  const integerPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  
  return integerPart + '.' + parts[1];
};

/**
 * Handles input change for currency fields
 * Allows user to type naturally while maintaining format
 * @param {string} value - Raw input value
 * @returns {string} - Cleaned numeric value (without formatting)
 */
export const handleCurrencyChange = (value) => {
  // Remove all non-numeric characters except decimal point and minus
  const cleaned = String(value).replace(/[^0-9.-]/g, '');
  return cleaned;
};
